import React from 'react';
import { MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';
import '../styles/pages/_projects.scss';

const Projects = () => {
  return (
    <div className="projects-container">
      <h2 className="section-heading">Projects & Professional Experience</h2>

      <h3 className="featured-heading">Featured Projects</h3>
      <div className="showcase-grid">

        {/* Card 1 — Casino Platform */}
        <div className="showcase-card">
          <div className="showcase-card__header">
            <span className="showcase-card__title">Casino Platform</span>
            <span className="showcase-card__badge">Multiplayer Game</span>
          </div>
          <div className="showcase-card__body">
            <div className="showcase-card__tech">
              <span className="skill-tag">React</span>
              <span className="skill-tag">TypeScript</span>
              <span className="skill-tag">Canvas 2D</span>
              <span className="skill-tag">Socket.io</span>
              <span className="skill-tag">Express</span>
              <span className="skill-tag">SQLite</span>
            </div>
            <p className="showcase-card__description">
              Real-time multiplayer casino with four card games — Egyptian Ratscrew, Gin Rummy,
              Speed, and Texas Hold'em. Players walk a pixel art lobby, sit at tables, and compete
              with chip wagers. Battle UI wraps every game with avatar portraits, chip bars, and VS
              splash animations.
            </p>
          </div>
          <div className="showcase-card__footer">
            <Link to="/casino" className="showcase-card__link">Enter Casino →</Link>
          </div>
        </div>

        {/* Card 2 — Word Hunter */}
        <div className="showcase-card">
          <div className="showcase-card__header">
            <span className="showcase-card__title">Word Hunter</span>
            <span className="showcase-card__badge">Typing Game</span>
          </div>
          <div className="showcase-card__body">
            <div className="showcase-card__tech">
              <span className="skill-tag">React</span>
              <span className="skill-tag">TypeScript</span>
              <span className="skill-tag">Canvas 2D</span>
              <span className="skill-tag">OKLCH Design System</span>
            </div>
            <p className="showcase-card__description">
              Fast-paced typing game where word enemies swarm toward your cursor and you defeat them
              by typing their names. Features wave-based progression with three enemy types (homing,
              streak, bomb), a safe zone mechanic, text-selection walls, and a pixel art character
              with contextual dialogue.
            </p>
          </div>
          <div className="showcase-card__footer">
            <Link to="/" className="showcase-card__link">Play Word Hunter →</Link>
          </div>
        </div>

        {/* Card 3 — PM Dashboard */}
        <div className="showcase-card">
          <div className="showcase-card__header">
            <span className="showcase-card__title">PM Dashboard</span>
            <span className="showcase-card__badge">Internal Tool</span>
          </div>
          <div className="showcase-card__body">
            <div className="showcase-card__tech">
              <span className="skill-tag">React</span>
              <span className="skill-tag">TypeScript</span>
              <span className="skill-tag">Canvas 2D</span>
              <span className="skill-tag">D3</span>
              <span className="skill-tag">Socket.io</span>
              <span className="skill-tag">SQLite</span>
              <span className="skill-tag">Slack API</span>
              <span className="skill-tag">GitHub API</span>
              <span className="skill-tag">OpenRouter AI</span>
            </div>
            <p className="showcase-card__description">
              Private project management dashboard at /pm — tracks team tasks, syncs GitHub commits,
              receives Slack real-time events, and renders the whole team as walking pixel art
              characters in a Canvas 2D office scene. AI-generated learning modules from commit
              history.
            </p>
          </div>
          <div className="showcase-card__footer">
            <span className="showcase-card__private">Private — IP-restricted</span>
          </div>
        </div>

      </div>

      <h3 className="other-heading">Other Projects</h3>
      <div className="timeline">

        <div className="timeline-item timeline-item-left">
          <div className="timeline-content">
            <div className="timeline-date">December 2024</div>
            <h3 className="timeline-role">Egyptian Ratscrew Card Game</h3>
            <h4 className="timeline-company">Personal Project</h4>
            <div className="timeline-location">
              <MapPin size={16} />
              Interactive Browser Game
            </div>
            <div className="skill-tags">
              <span className="skill-tag">Canvas 2D</span>
              <span className="skill-tag">TypeScript</span>
              <span className="skill-tag">Game Development</span>
              <span className="skill-tag">React Integration</span>
            </div>
            <ul className="timeline-achievements">
              <li>Built an interactive card game using <span className="skill-highlight">Canvas 2D</span> and TypeScript for enhanced type safety.</li>
              <li>Implemented custom game logic including slap mechanics, challenge systems, and configurable rule sets.</li>
              <li>Integrated seamlessly into a React application, managing game lifecycle and cleanup.</li>
              <li>Designed responsive UI with mobile touch support and keyboard controls for desktop gameplay.</li>
            </ul>
            <div className="project-link-container">
              <Link to="/egyptian-ratscrew" className="play-game-button">
                Play Game →
              </Link>
            </div>
          </div>
        </div>

        <div className="timeline-item timeline-item-right">
          <div className="timeline-content">
            <div className="timeline-date">December 2024</div>
            <h3 className="timeline-role">Nest Navigate - Interactive Learning Module</h3>
            <h4 className="timeline-company">Educational Platform for Homebuyers</h4>
            <a href="https://nest-navigate-assignment.vercel.app/" className="link" target="_blank" rel="noopener noreferrer">View Live Application</a>
            <div className="timeline-location">
              <MapPin size={16} />
              Personal Project
            </div>
            <div className="skill-tags">
              <span className="skill-tag">React 18</span>
              <span className="skill-tag">Vite</span>
              <span className="skill-tag">Tailwind CSS</span>
              <span className="skill-tag">Lucide React</span>
              <span className="skill-tag">Gamification</span>
              <span className="skill-tag">LocalStorage API</span>
              <span className="skill-tag">Responsive Design</span>
            </div>
            <p className="project-description">
              Developed a <span className="skill-highlight">gamified learning platform</span> for first-time homebuyers focused on understanding home inspections.
            </p>
            <ul className="timeline-achievements">
              <li>Created <span className="skill-highlight">3 comprehensive interactive lessons</span> covering home inspection fundamentals, types of inspections, and red flags to watch for.</li>
              <li>Implemented <span className="skill-highlight">gamification elements</span> including coin rewards, progress tracking, and an achievement ranking system.</li>
              <li>Built with <span className="skill-highlight">React 18 and Vite</span> for optimal performance, utilizing React Hooks for state management and LocalStorage for progress persistence.</li>
              <li>Designed a <span className="skill-highlight">mobile-first responsive interface</span> with smooth animations and transitions using Tailwind CSS.</li>
            </ul>
          </div>
        </div>

        <div className="timeline-item timeline-item-left">
          <div className="timeline-content">
            <div className="timeline-date">March 2025</div>
            <h3 className="timeline-role">Pantry Pal - Smart Kitchen Management Platform</h3>
            <h4 className="timeline-company">Full-Stack Web Application</h4>
            <a href="https://foodpantry-pal.netlify.app/" className="link">Pantry Pal Prototype</a>
            <div className="timeline-location">
              <MapPin size={16} />
              Personal Project
            </div>
            <div className="skill-tags">
              <span className="skill-tag">React 19</span>
              <span className="skill-tag">Firebase</span>
              <span className="skill-tag">Google Gemini AI</span>
              <span className="skill-tag">Tailwind CSS</span>
              <span className="skill-tag">Vite</span>
              <span className="skill-tag">PWA</span>
              <span className="skill-tag">JavaScript ES6+</span>
            </div>
            <p className="project-description">
              Intelligent cooking companion combining modern web technologies with <span className="skill-highlight">AI-powered recipe generation</span> to reduce food waste and spark culinary creativity.
            </p>
            <ul className="timeline-achievements">
              <li>Built with <span className="skill-highlight">React 19 and Vite</span>, using <span className="skill-highlight">Firebase</span> for real-time data synchronization and authentication.</li>
              <li>Integrated <span className="skill-highlight">Google's Gemini 2.0 Flash API</span> for intelligent recipe generation based on available ingredients.</li>
              <li>Implemented <span className="skill-highlight">PWA functionality</span> with offline capabilities through service workers.</li>
            </ul>
          </div>
        </div>

        <div className="timeline-item timeline-item-left video-timeline-item">
          <div className="timeline-content video-content">
            <h3 className="timeline-role">Project Showcase Video</h3>
            <div className="video-container">
              <iframe
                width="100%"
                height="250"
                src="https://www.youtube.com/embed/u8g9pNwYBTQ?autoplay=1&mute=1"
                title="Pantry Pal Showcase"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
            <p className="video-caption">Video demonstration of the Pantry Pal Web Application</p>
          </div>
        </div>

        <div className="timeline-item timeline-item-right">
          <div className="timeline-content">
            <div className="timeline-date">January 2025 - March 2025</div>
            <h3 className="timeline-role">Website Redesign & Development Contractor</h3>
            <h4 className="timeline-company">Mitchell First Circuit CASA</h4>
            <div className="timeline-location">
              <MapPin size={16} />
              Remote
            </div>
            <div className="skill-tags">
              <span className="skill-tag">HTML</span>
              <span className="skill-tag">CSS</span>
              <span className="skill-tag">JavaScript</span>
              <span className="skill-tag">Figma</span>
              <span className="skill-tag">Adobe After Effects</span>
            </div>
            <ul className="timeline-achievements">
              <li>Redesigned and developed a responsive website using HTML, CSS, and JavaScript, improving accessibility and user experience.</li>
              <li>Used Figma to design the site's layout and visual elements, ensuring a user-friendly experience.</li>
              <li>Enhanced navigation and added interactive elements to effectively engage volunteers and donors.</li>
              <li>Created a video in Adobe After Effects to showcase the redesign process.</li>
            </ul>
          </div>
        </div>

        <div className="timeline-item timeline-item-right video-timeline-item">
          <div className="timeline-content video-content">
            <h3 className="timeline-role">Project Showcase Video</h3>
            <div className="video-container">
              <iframe
                width="100%"
                height="250"
                src="https://www.youtube.com/embed/l3joHmHmnhc?autoplay=1&mute=1"
                title="Website Redesign Showcase"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
            <p className="video-caption">Video demonstration of the Mitchell First Circuit CASA website redesign process</p>
          </div>
        </div>

        <div className="timeline-item timeline-item-right">
          <div className="timeline-content">
            <div className="timeline-date">May 2024 - July 2024</div>
            <h3 className="timeline-role">Solutions Developer</h3>
            <h4 className="timeline-company">Leading Edge Construction</h4>
            <div className="timeline-location">
              <MapPin size={16} />
              Los Banos, CA
            </div>
            <div className="skill-tags">
              <span className="skill-tag">AI Solutions</span>
              <span className="skill-tag">Presentations</span>
              <span className="skill-tag">Workflow Integration</span>
            </div>
            <ul className="timeline-achievements">
              <li>Conducted presentations to the office team on <span className="skill-highlight">AI applications</span> in the workplace; discussed <span className="skill-highlight">AI integration</span> into their workflow.</li>
            </ul>
          </div>
        </div>

        <div className="timeline-item timeline-item-left">
          <div className="timeline-content">
            <div className="timeline-date">November 2023 - April 2024</div>
            <h3 className="timeline-role">Application Developer</h3>
            <h4 className="timeline-company">Equity Justice and Inclusive Excellence Department at UC Merced</h4>
            <div className="timeline-location">
              <MapPin size={16} />
              University of California, Merced, CA
            </div>
            <div className="skill-tags">
              <span className="skill-tag">Flutterflow</span>
              <span className="skill-tag">Interactive Tutorials</span>
              <span className="skill-tag">YouTube API</span>
              <span className="skill-tag">Mobile App Development</span>
            </div>
            <ul className="timeline-achievements">
              <li>Collaborated with a team to develop an interactive learning module app for the EJIE department using <span className="skill-highlight">Flutterflow</span>, featuring interactive tutorials, timed readings, linked articles, <span className="skill-highlight">YouTube API integration</span>, and quizzes.</li>
            </ul>
          </div>
        </div>

        <div className="timeline-item timeline-item-right">
          <div className="timeline-content">
            <div className="timeline-date">August 2023 - December 2023</div>
            <h3 className="timeline-role">Web App Developer</h3>
            <h4 className="timeline-company">Sunburst Agri Biotech Solutions LLC</h4>
            <div className="timeline-location">
              <MapPin size={16} />
              University of California, Merced, CA
            </div>
            <div className="skill-tags">
              <span className="skill-tag">Computer Vision</span>
              <span className="skill-tag">CNN</span>
              <span className="skill-tag">Machine Learning</span>
              <span className="skill-tag">Image Processing</span>
              <span className="skill-tag">Cross-platform</span>
            </div>
            <ul className="timeline-achievements">
              <li>Designed and implemented a <span className="skill-highlight">computer vision model</span> utilizing <span className="skill-highlight">Convolutional Neural Networks (CNN)</span> to classify pathogen types with high accuracy.</li>
              <li>Integrated image processing and machine learning techniques into a <span className="skill-highlight">cross-platform application</span> delivering a user-friendly web and desktop solution for real-time pathogen identification.</li>
            </ul>
          </div>
        </div>

      </div>
    </div>
  );
};

export default Projects;
